import { useState, useRef, useEffect } from "react";
import { LegalLinks } from "../components/LegalLinks";
import { useLaunchConfig } from "../lib/launch-config";
import { track } from "../lib/analytics";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { T } from "../tokens";
import type { ResumeClarity } from "@jobreel/shared";
import { Btn }   from "../components/Btn";
import { Badge } from "../components/Badge";
import { PasswordField } from "../components/PasswordField";
import { LiveDot } from "../components/LiveDot";
import { ResumeClarityCallout } from "../components/ResumeClarityCallout";
import { TargetRoleEditor, WORK_MODE_TAG_VALUES } from "../components/TargetRoleEditor";
import { useReveal } from "../hooks/useMotion";
import { useAuthStore } from "../store";
import { GOOGLE_OAUTH_START } from "../components/GoogleOAuthButton";

const WORK_AUTH_TAGS = [
  "remote-from-india", "oci-friendly", "relocation-sponsored",
  "h1b-transfer", "india-onsite", "hybrid-india", "contract-remote",
];

const SEGMENTS = [
  { value: "india-global-remote", label: "India — global remote" },
  { value: "return-to-india",     label: "Return to India" },
  { value: "senior-non-it",       label: "Senior non-IT" },
  { value: "freshly-returned",    label: "Freshly returned" },
  { value: "diaspora",            label: "Diaspora" },
  { value: "senior-freelancer",   label: "Senior freelancer" },
];

type ActiveResume = {
  createdAt?: string;
  clarity?: ResumeClarity | null;
  targetRoles?: string[] | null;
  targetLocations?: string[] | null;
  workAuthTags?: string[] | null;
  seniorityLevel?: string | null;
  structuredJson?: {
    name?: string;
    email?: string;
    summary?: string;
    skills?: string[];
    experience?: Array<{ company?: string; title?: string; startDate?: string; endDate?: string; description?: string }>;
    education?: Array<{ institution?: string; degree?: string; field?: string; year?: string | null }>;
  } | null;
};

function Card({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return (
    <div style={{ background: T.s1, border: `1px solid ${T.b0}`, borderRadius: "10px",
      padding: "20px", ...style }}>
      {children}
    </div>
  );
}

function CardLabel({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ fontFamily: T.mono, fontSize: "9px", color: T.t3, letterSpacing: "0.10em",
      textTransform: "uppercase", marginBottom: "10px" }}>
      {children}
    </div>
  );
}

export function Profile() {
  const user = useAuthStore(s => s.user);
  const qc   = useQueryClient();

  const fileRef   = useRef<HTMLInputElement>(null);
  const targetRoleInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadMsg, setUploadMsg] = useState<string | null>(null);
  // Explicit, not inferred from the message text (hardening/resume-ux P0-5):
  // sniffing uploadMsg for the substring "failed" mis-colored the 429
  // "You've reached the resume upload limit…" message as success, since that
  // sentence never contains that word.
  const [uploadIsError, setUploadIsError] = useState(false);
  const [replacing, setReplacing] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordMsg, setPasswordMsg] = useState<string | null>(null);
  // Danger zone — self-service account deletion (POST /api/auth/delete-account).
  const [deleteConfirm, setDeleteConfirm] = useState("");
  const [deletePassword, setDeletePassword] = useState("");
  const [deleteMsg, setDeleteMsg] = useState<string | null>(null);
  const [deleteNeedsGoogle, setDeleteNeedsGoogle] = useState(false);
  const logout = useAuthStore(s => s.logout);
  const launch = useLaunchConfig();
  const [exportError, setExportError] = useState<string | null>(null);

  const headerRev = useReveal(0, 10);
  const card1Rev  = useReveal(80, 8);
  const card2Rev  = useReveal(160, 8);
  const card3Rev  = useReveal(240, 8);
  const card4Rev  = useReveal(320, 8);

  const activeResumeQuery = useQuery({
    queryKey: ["active-resume"],
    queryFn: async () => {
      const { data } = await axios.get<ActiveResume>("/api/resumes/active");
      return data;
    },
    retry: false,
  });

  const noActiveResume = activeResumeQuery.isError
    && (activeResumeQuery.error as { response?: { status?: number } })?.response?.status === 404;

  // The Matches/Dashboard "Choose target role" CTA links to /profile#target-role —
  // scroll that section into view and focus the primary-role field so the link
  // lands somewhere specific instead of a generic Profile page with no visible
  // action. Runs once the resume (and therefore the section) has rendered.
  useEffect(() => {
    if (window.location.hash !== "#target-role" || !activeResumeQuery.isSuccess) return;
    const el = document.getElementById("target-role");
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "start" });
    targetRoleInputRef.current?.focus();
  }, [activeResumeQuery.isSuccess]);

  const prefMutation = useMutation({
    mutationFn: async (prefs: Record<string, unknown>) => {
      const { data } = await axios.patch("/api/resumes/preferences", prefs);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["me"] }),
  });

  const changePasswordMutation = useMutation({
    mutationFn: async () => {
      const { data } = await axios.post("/api/auth/change-password", {
        currentPassword,
        newPassword,
      });
      return data;
    },
    onSuccess: () => {
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setPasswordMsg("Password updated successfully.");
    },
    onError: (err: any) => {
      setPasswordMsg(err?.response?.data?.error ?? "Failed to update password.");
    },
  });

  const deleteAccountMutation = useMutation({
    mutationFn: async () => {
      const { data } = await axios.post("/api/auth/delete-account", {
        confirm: deleteConfirm,
        ...(deletePassword ? { password: deletePassword } : {}),
      });
      return data;
    },
    onSuccess: () => {
      // The account and every session are gone: clear local state and leave.
      qc.clear();
      logout();
      window.location.assign("/");
    },
    onError: (err: any) => {
      const code = err?.response?.data?.code;
      setDeleteNeedsGoogle(code === "reauthentication_required");
      setDeleteMsg(err?.response?.data?.error ?? "Could not delete the account.");
    },
  });

  const canSubmitPassword = currentPassword.length > 0
    && newPassword.length >= 8
    && newPassword === confirmPassword
    && !changePasswordMutation.isPending;

  const passwordInputStyle: React.CSSProperties = {
    width: "100%",
    padding: "10px 12px",
    background: T.s2,
    border: `1px solid ${T.b1}`,
    borderRadius: "7px",
    color: T.t0,
    fontFamily: T.body,
    fontSize: "13px",
    outline: "none",
  };

  const handleChangePassword = (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordMsg(null);
    if (newPassword !== confirmPassword) {
      setPasswordMsg("New password and confirm password do not match.");
      return;
    }
    changePasswordMutation.mutate();
  };

  async function handleFileUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setUploadMsg(null);
    setUploadIsError(false);
    setReplacing(false);
    try {
      const fd = new FormData();
      fd.append("resume", file);
      await axios.post("/api/resumes", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setUploadMsg("Resume parsed and activated. Match scores will update shortly.");
      // hardening/resume-ux P0-3: this success message promises matches will
      // update, but the keys actually read by ResumeGate/Dashboard/Matches
      // (["resume","active"], see hooks/useResume.ts) and by
      // MatchReadinessPanel (["candidate-readiness"]) were never invalidated
      // here — only this page's own ["active-resume"] and ["me"] were, so a
      // replace left the rest of the app showing the PRIOR resume/readiness
      // until an unrelated refetch happened to occur.
      qc.invalidateQueries({ queryKey: ["me"] });
      qc.invalidateQueries({ queryKey: ["active-resume"] });
      qc.invalidateQueries({ queryKey: ["resume", "active"] });
      qc.invalidateQueries({ queryKey: ["candidate-readiness"] });
      qc.invalidateQueries({ queryKey: ["matches"] });
      qc.invalidateQueries({ queryKey: ["matches-page"] });
      qc.invalidateQueries({ queryKey: ["match-stats"] });
    } catch (err: unknown) {
      // hardening/resume-ux P0-5: resumes.ts's own error responses
      // (400/422 — "No file uploaded", "Only PDF and DOCX are supported",
      // "Could not extract text from document", "Resume parsing failed")
      // use `.error`, not `.message` — only the kill-switch (403) and budget
      // (429) rejections carry `.message`. Reading `.message` alone silently
      // dropped every parse/validation failure into the generic fallback.
      const data = (err as { response?: { data?: { message?: string; error?: string } } })?.response?.data;
      setUploadMsg(data?.message ?? data?.error ?? "Upload failed. Please try again.");
      setUploadIsError(true);
    } finally {
      setUploading(false);
    }
  }

  const resume = activeResumeQuery.data;
  const hasResume = activeResumeQuery.isSuccess && !!resume;

  const uploadedDate = resume?.createdAt
    ? new Date(resume.createdAt).toLocaleDateString("en-US", { day: "numeric", month: "short", year: "numeric" })
    : null;

  return (
    <div style={{ maxWidth: "680px" }}>

      {/* ── Header ───────────────────────────────────────────────── */}
      <div style={{ ...headerRev, marginBottom: "28px" }}>
        <div style={{ fontFamily: T.mono, fontSize: "10px", color: T.t3,
          letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: "6px" }}>
          {user?.email}
        </div>
        <h1 style={{ fontFamily: T.display, fontSize: "26px", fontWeight: 700, color: T.t0, margin: 0 }}>
          Resume Intelligence
        </h1>
        <p style={{ fontFamily: T.body, fontSize: "13px", color: T.t2, marginTop: "4px" }}>
          Your active resume drives all match scoring.
        </p>
        <p style={{ fontFamily: T.body, fontSize: "11px", color: T.t3, marginTop: "6px", lineHeight: 1.5 }}>
          Caliber is in Beta. Your resume information is processed to generate job matches;
          recommendations may be incomplete or inaccurate, and some role categories may have
          limited inventory. {launch.flags.honestCopy
            ? (launch.flags.dataExport
              ? "It works best for technology roles in India; other fields are less tested. You can download your data or delete your account below."
              : "It works best for technology roles in India; other fields are less tested. You can delete your account below.")
            : "Contact support to request account or resume deletion."}
        </p>
      </div>

      <ResumeClarityCallout clarity={resume?.clarity} />

      {/* ── Resume status ────────────────────────────────────────── */}
      <div style={{ ...card1Rev, marginBottom: "12px" }}>
        <Card>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "14px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              {hasResume
                ? <LiveDot color={T.ok} size={6}/>
                : <span style={{ width: 6, height: 6, borderRadius: "50%", background: T.t3, display: "inline-block" }}/>
              }
              <span style={{ fontFamily: T.mono, fontSize: "11px", color: hasResume ? T.ok : T.t2, fontWeight: 600 }}>
                {hasResume ? "Resume active" : "No resume uploaded"}
              </span>
              {uploadedDate && (
                <span style={{ fontFamily: T.body, fontSize: "11px", color: T.t3 }}>
                  · uploaded {uploadedDate}
                </span>
              )}
            </div>
          </div>

          <input ref={fileRef} type="file" accept=".pdf,.docx" style={{ display: "none" }} onChange={handleFileUpload} />

          {replacing && hasResume && (
            <div style={{ background: T.warnBg, border: `1px solid ${T.warnBdr}`, borderRadius: "6px",
              padding: "10px 14px", marginBottom: "12px" }}>
              <p style={{ fontFamily: T.body, fontSize: "12px", color: T.warn, margin: 0 }}>
                Replacing your resume will re-parse and re-score all your matches. This cannot be undone.
              </p>
            </div>
          )}

          <div style={{ display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap" }}>
            {!replacing && hasResume
              ? <Btn variant="outline" size="sm" onClick={() => setReplacing(true)}>Replace resume</Btn>
              : <Btn variant={hasResume ? "teal" : "outline"} size="sm"
                  onClick={() => fileRef.current?.click()} disabled={uploading}>
                  {uploading ? "Uploading…" : hasResume ? "Choose new file" : "Upload PDF / DOCX"}
                </Btn>
            }
            {replacing && (
              <Btn variant="ghost" size="sm" onClick={() => setReplacing(false)}>Cancel</Btn>
            )}
            {uploadMsg && (
              <span style={{ fontFamily: T.body, fontSize: "12px",
                color: uploadIsError ? T.err : T.ok }}>
                {uploadMsg}
              </span>
            )}
          </div>
        </Card>
      </div>

      {/* ── Parsed resume data ───────────────────────────────────── */}
      {activeResumeQuery.isLoading && (
        <Card style={{ marginBottom: "12px" }}>
          <p style={{ fontFamily: T.body, fontSize: "12px", color: T.t2, margin: 0 }}>Loading resume data…</p>
        </Card>
      )}

      {noActiveResume && (
        <Card style={{ marginBottom: "12px", textAlign: "center", padding: "32px" }}>
          <p style={{ fontFamily: T.display, fontSize: "16px", color: T.t1, margin: "0 0 6px" }}>No resume yet</p>
          <p style={{ fontFamily: T.body, fontSize: "13px", color: T.t2, margin: 0 }}>
            Upload a PDF or DOCX to activate match scoring.
          </p>
        </Card>
      )}

      {hasResume && resume && (
        <>
          {/* Identity + summary */}
          <div style={{ ...card2Rev, marginBottom: "12px" }}>
            <Card>
              <CardLabel>Identity</CardLabel>
              <div style={{ fontFamily: T.display, fontSize: "18px", fontWeight: 700, color: T.t0, marginBottom: "2px" }}>
                {resume.structuredJson?.name ?? user?.email ?? "—"}
              </div>
              {resume.structuredJson?.email && resume.structuredJson.email !== user?.email && (
                <div style={{ fontFamily: T.body, fontSize: "12px", color: T.t2, marginBottom: "12px" }}>
                  {resume.structuredJson.email}
                </div>
              )}

              {resume.structuredJson?.summary && (
                <>
                  <div style={{ height: "1px", background: T.b0, margin: "12px 0" }}/>
                  <CardLabel>Summary</CardLabel>
                  <p style={{ fontFamily: T.body, fontSize: "13px", color: T.t1, margin: 0, lineHeight: 1.65 }}>
                    {resume.structuredJson.summary}
                  </p>
                </>
              )}
            </Card>
          </div>

          {/* Skills + targets */}
          <div style={{ ...card3Rev, display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", marginBottom: "12px" }}>
            <Card>
              <CardLabel>Skills</CardLabel>
              <div style={{ display: "flex", gap: "5px", flexWrap: "wrap" }}>
                {(resume.structuredJson?.skills?.length ? resume.structuredJson.skills : ["—"]).map((s, i) => (
                  <Badge key={`${s}-${i}`} variant="default" size="sm">{s}</Badge>
                ))}
              </div>
            </Card>
            <Card>
              <CardLabel>Target roles</CardLabel>
              <div style={{ display: "flex", gap: "5px", flexWrap: "wrap", marginBottom: "10px" }}>
                {(resume.targetRoles?.length ? resume.targetRoles : ["—"]).map((r, i) => (
                  <Badge key={`${r}-${i}`} variant="teal" size="sm">{r}</Badge>
                ))}
              </div>
              <CardLabel>Target locations</CardLabel>
              <div style={{ display: "flex", gap: "5px", flexWrap: "wrap" }}>
                {(resume.targetLocations?.length ? resume.targetLocations : ["—"]).map((l, i) => (
                  <Badge key={`${l}-${i}`} variant="info" size="sm">{l}</Badge>
                ))}
              </div>
            </Card>
          </div>

          {/* Job Preferences / Target Role — editable. This is what the "Choose
              target role" link from Matches/Dashboard scrolls to and focuses. */}
          <div id="target-role" style={{ marginBottom: "12px", scrollMarginTop: "20px" }}>
            <Card>
              <CardLabel>Job Preferences / Target Role</CardLabel>
              <TargetRoleEditor
                primaryInputRef={targetRoleInputRef}
                initial={{
                  primaryRole:       resume.targetRoles?.[0] ?? "",
                  secondaryRoles:    (resume.targetRoles ?? []).slice(1),
                  locations:         resume.targetLocations ?? [],
                  workMode:          (resume.workAuthTags ?? []).find((t) => WORK_MODE_TAG_VALUES.includes(t)) ?? "",
                  seniorityLevel:    resume.seniorityLevel ?? "",
                  otherWorkAuthTags: (resume.workAuthTags ?? []).filter((t) => !WORK_MODE_TAG_VALUES.includes(t)),
                }}
              />
            </Card>
          </div>

          {/* Experience */}
          {resume.structuredJson?.experience?.length ? (
            <div style={{ marginBottom: "12px" }}>
              <Card>
                <CardLabel>Experience</CardLabel>
                <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                  {resume.structuredJson.experience.map((e, i) => (
                    <div key={i} style={{ display: "flex", gap: "12px", alignItems: "flex-start" }}>
                      <div style={{ width: "3px", borderRadius: "2px", background: i === 0 ? T.teal : T.b1,
                        alignSelf: "stretch", flexShrink: 0 }}/>
                      <div>
                        <div style={{ fontFamily: T.display, fontSize: "13px", fontWeight: 600, color: T.t0 }}>
                          {e.title ?? "—"}{e.company ? ` @ ${e.company}` : ""}
                        </div>
                        {(e.startDate || e.endDate) && (
                          <div style={{ fontFamily: T.mono, fontSize: "10px", color: T.t3, marginTop: "2px" }}>
                            {e.startDate ?? "?"} – {e.endDate ?? "present"}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            </div>
          ) : null}

          {/* Education */}
          {resume.structuredJson?.education?.length ? (
            <div style={{ marginBottom: "12px" }}>
              <Card>
                <CardLabel>Education</CardLabel>
                <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                  {resume.structuredJson.education.map((e, i) => (
                    <div key={i} style={{ fontFamily: T.body, fontSize: "12px", color: T.t1 }}>
                      {e.degree ?? ""}{e.degree && e.institution ? " — " : ""}{e.institution ?? ""}
                      {e.field ? ` (${e.field})` : ""}
                      {e.year ? `, ${e.year}` : ""}
                    </div>
                  ))}
                </div>
              </Card>
            </div>
          ) : null}
        </>
      )}

      {/* ── Preferences ──────────────────────────────────────────── */}
      <div style={{ ...card4Rev }}>
        <Card style={{ marginBottom: "12px" }}>
          <CardLabel>Reset password</CardLabel>
          <form onSubmit={handleChangePassword} style={{ display: "grid", gap: "8px", marginBottom: "8px" }}>
            <PasswordField
              value={currentPassword}
              onChange={setCurrentPassword}
              placeholder="Current password"
              autoComplete="current-password"
              style={passwordInputStyle}
            />
            <PasswordField
              value={newPassword}
              onChange={setNewPassword}
              placeholder="New password (min 8 chars)"
              autoComplete="new-password"
              style={passwordInputStyle}
            />
            <PasswordField
              value={confirmPassword}
              onChange={setConfirmPassword}
              placeholder="Confirm new password"
              autoComplete="new-password"
              style={passwordInputStyle}
            />
            <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
              <Btn type="submit" variant="outline" size="sm" disabled={!canSubmitPassword}>
                {changePasswordMutation.isPending ? "Updating…" : "Update password"}
              </Btn>
              {passwordMsg && (
                <span style={{ fontFamily: T.body, fontSize: "12px", color: passwordMsg.toLowerCase().includes("success") ? T.ok : T.err }}>
                  {passwordMsg}
                </span>
              )}
            </div>
          </form>
        </Card>

        {launch.flags.dataExport && (
          <Card style={{ marginBottom: "12px" }}>
            <CardLabel>Your data</CardLabel>
            <p style={{ fontFamily: T.body, fontSize: "12px", color: T.t2, marginTop: 0 }}>
              Download a copy of everything Calibre stores about you (profile, résumé text, matches, tracker, captured jobs) as a JSON file.
            </p>
            <Btn type="button" variant="outline" size="sm" data-testid="export-data" onClick={async () => {
              setExportError(null);
              try {
                const { data } = await axios.get("/api/account/export");
                const url = URL.createObjectURL(new Blob([JSON.stringify(data, null, 2)], { type: "application/json" }));
                const a = document.createElement("a");
                a.href = url; a.download = "calibre-my-data.json"; document.body.appendChild(a); a.click(); a.remove();
                setTimeout(() => URL.revokeObjectURL(url), 1000);
                track("data_exported");
              } catch (err) {
                const status = (err as { response?: { status?: number } })?.response?.status;
                setExportError(status === 429 ? "Please wait a minute before downloading again." : "We couldn't prepare your download. Please try again in a moment.");
              }
            }}>Download my data</Btn>
            {exportError && <p role="alert" style={{ fontFamily: T.body, fontSize: "12px", color: T.err, margin: "8px 0 0" }}>{exportError}</p>}
          </Card>
        )}

        <Card style={{ marginBottom: "12px", border: `1px solid ${T.err}` }}>
          <CardLabel>Delete account</CardLabel>
          <p style={{ fontFamily: T.body, fontSize: "12px", color: T.t2, marginTop: 0 }}>
            Permanently deletes your account, résumé, matches, applications and payment records. This cannot be undone.
            If you signed up with Google, you may be asked to sign in with Google again first.
          </p>
          <form
            onSubmit={(e) => { e.preventDefault(); setDeleteMsg(null); deleteAccountMutation.mutate(); }}
            style={{ display: "grid", gap: "8px" }}
            data-testid="delete-account-form"
          >
            <input
              value={deleteConfirm}
              onChange={(e) => setDeleteConfirm(e.target.value)}
              placeholder="Type DELETE to confirm"
              aria-label="Type DELETE to confirm"
              autoComplete="off"
              style={passwordInputStyle}
            />
            <PasswordField
              value={deletePassword}
              onChange={setDeletePassword}
              placeholder="Your password (leave blank only if you sign in with Google alone)"
              autoComplete="current-password"
              style={passwordInputStyle}
            />
            <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
              <Btn type="submit" variant="outline" size="sm" disabled={deleteConfirm !== "DELETE" || deleteAccountMutation.isPending}>
                {deleteAccountMutation.isPending ? "Deleting…" : "Delete my account"}
              </Btn>
              {deleteNeedsGoogle && (
                <Btn type="button" variant="outline" size="sm" onClick={() => {
                  // Come back to this card after Google; best-effort (storage may be blocked).
                  try { sessionStorage.setItem("calibre_post_oauth_path", "/profile"); } catch { /* ignore */ }
                  window.location.assign(`${GOOGLE_OAUTH_START}?reauth=1`);
                }}>
                  Sign in with Google again
                </Btn>
              )}
              {deleteMsg && (
                <span style={{ fontFamily: T.body, fontSize: "12px", color: T.err }}>{deleteMsg}</span>
              )}
            </div>
          </form>
        </Card>

        <Card style={{ marginBottom: "12px" }}>
          <CardLabel>Work authorisation</CardLabel>
          <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
            {WORK_AUTH_TAGS.map(tag => (
              <Badge key={tag} variant="default" size="sm">{tag}</Badge>
            ))}
          </div>
          <p style={{ fontFamily: T.body, fontSize: "11px", color: T.t3, marginTop: "10px", marginBottom: 0 }}>
            Matched against job postings to surface the most relevant results.
          </p>
        </Card>

        <Card>
          <CardLabel>Segment</CardLabel>
          <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
            {SEGMENTS.map(s => (
              <Badge
                key={s.value}
                variant={user?.segment === s.value ? "teal" : "default"}
                size="md"
                style={{ cursor: "pointer" }}
                onClick={() => prefMutation.mutate({ segment: s.value })}
              >{s.label}</Badge>
            ))}
          </div>
        </Card>
      </div>
      <LegalLinks />
    </div>
  );
}
